import { AxiosResponse } from 'axios';
import api from '../config/api';
import { Content } from '../models/content/types';
import { HomepageModuleParams } from '../common/queries/use-content-feed-query';

interface FeedServiceOptions {
  query?: string;
  filter?: string;
  channelId?: string;
  advocateId?: string;
  advocateFilter?: string;
  includeTypes?: string[];
  initiativeId?: string;
  mention_format?: 'raw' | 'human_readable' | 'display_name' | 'with_metadata';
  useLegacyMetadata?: boolean;
  homepageModuleParams?: HomepageModuleParams;
}

type FeedData = {
  type: string;
  attributes: Content;
};

type LegacyMetadata = {
  page: {
    number: number;
    size: number;
  };
  total?: number;
  total_pages?: number;
  has_more?: boolean;
};

type Metadata = {
  page: number;
  total_results: number;
  total_pages: number;
  per_page: number;
};

type RawServiceResponse =
  | {
      feed: FeedData[];
      contents?: never;
      meta: Metadata | LegacyMetadata;
    }
  | { feed?: never; contents: FeedData[]; meta: Metadata | LegacyMetadata };

type FeedServiceMetadata = {
  page: {
    number: number;
    size: number;
  };
  // The search endpoint reports a count of matches; the feed endpoint reports only
  // whether another page exists, because counting a whole feed scope costs the database
  // more than fetching the page does. Leave each undefined when the backend did not
  // report it, so consumers can distinguish "missing" from a valid 0 / false.
  total?: number;
  total_pages?: number;
  has_more?: boolean;
};

export type FeedServiceResponse =
  | {
      feed: FeedData[];
      contents?: never;
      meta: FeedServiceMetadata;
    }
  | { feed?: never; contents: FeedData[]; meta: FeedServiceMetadata };

class FeedService {
  readonly query?: string;
  readonly filter?: string;
  readonly channelId?: string;
  readonly advocateId?: string;
  readonly advocateFilter?: string;
  readonly includeTypes?: string[];
  readonly initiativeId?: string;
  readonly mention_format?: FeedServiceOptions['mention_format'];
  readonly useLegacyMetadata: boolean;
  readonly homepageModuleParams?: HomepageModuleParams;

  constructor({
    query,
    filter,
    channelId,
    advocateId,
    advocateFilter,
    includeTypes,
    initiativeId,
    mention_format,
    useLegacyMetadata = true,
    homepageModuleParams,
  }: FeedServiceOptions = {}) {
    this.query = query;
    this.filter = filter;
    this.channelId = channelId;
    this.initiativeId = initiativeId;
    this.advocateId = advocateId;
    this.advocateFilter = advocateFilter;
    this.includeTypes = includeTypes;
    this.mention_format = mention_format;
    this.useLegacyMetadata = useLegacyMetadata;
    this.homepageModuleParams = homepageModuleParams;
  }

  fetch = async ({
    page,
    perPage,
    pinned,
  }: { page?: number; perPage?: number; pinned?: boolean } = {}) => {
    return api
      .get<RawServiceResponse>(this.url, {
        params: {
          query: this.query,
          filter_type: this.filter,
          content_channel: this.channelId,
          initiative_tag_id: this.initiativeId,
          include_types: this.includeTypes,
          width: 768,
          page_size: perPage,
          page,
          pinned,
          mention_format: this.mention_format,
          ...this.homepageModuleParams, //only compatible with the `/feed` endpoint
        },
      })
      .then(this.transformResponse);
  };

  transformResponse = (
    res: AxiosResponse<RawServiceResponse>
  ): FeedServiceResponse => {
    // Only the search endpoint uses the new metadata style at the moment
    if (this.useLegacyMetadata || !this.query) {
      const meta = res.data.meta as LegacyMetadata;
      // Only derive total_pages when the backend actually emitted total. When
      // both are absent (page 2+ or cursor responses), leave them undefined so
      // downstream code can tell "missing" apart from "0 pages".
      const derivedTotalPages =
        meta.total != null
          ? Math.ceil(meta.total / (meta.page.size || 20))
          : undefined;
      const transformedMeta: FeedServiceMetadata = {
        page: meta.page,
        total: meta.total,
        total_pages: meta.total_pages ?? derivedTotalPages,
        has_more: meta.has_more,
      };

      return res.data.feed
        ? { feed: res.data.feed, meta: transformedMeta }
        : { contents: res.data.contents, meta: transformedMeta };
    } else {
      const meta = res.data.meta as Metadata;
      const transformedMeta: FeedServiceMetadata = {
        page: {
          number: meta.page,
          size: meta.per_page,
        },
        total: meta.total_results,
        total_pages: meta.total_pages,
      };

      return res.data.feed
        ? { feed: res.data.feed, meta: transformedMeta }
        : { contents: res.data.contents, meta: transformedMeta };
    }
  };

  get url() {
    if (this.query) return '/feed/search';

    if (this.advocateId)
      return `/profiles/${this.advocateId}/contents/${this.advocateFilter}`;

    return '/feed';
  }
}

export default FeedService;
